import * as React from 'react';
import Box from '@mui/material/Box';
import { Chip, Grid, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { keyframes } from '@mui/system';
import StyledButton from '../Common/StyledButton';
import { coachTypeOptions } from '../../types/coach';
import { useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/rootReducer';
import { onboardingActions } from '../../redux/onboarding/onboardingSlice';

const StyledChip = styled(Chip)({
  color: '#2A3C67',
  height: 46,
  borderRadius: 100,
  border: '1px solid #2A3C67',
});

const SelectedChip = styled(Chip)({
  color: '#FFF',
  height: 46,
  borderRadius: 100,
  border: '1px solid #2A3C67',
  backgroundColor: '#2A3C67',
  '&:hover': {
    backgroundColor: '#2A3C67',
  },
});

export const opacityKeyframe = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export default function CoachType({ handleNext }: { handleNext: () => void }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const firstName = useAppSelector((state: RootState) => state.coach.coach.firstName);
  const therapist = useAppSelector((state: RootState) => state.onboarding.therapist);
  const [currentIndex, setCurrentIndex] = useState(-1);

  const validationSchema = Yup.object().shape({
    coachType: Yup.array()
      .of(Yup.string().required(t('therapistOnboarding.validations.specialization')))
      .required(),
  });

  const formik = useFormik({
    initialValues: { coachType: therapist.coachTypes },
    validationSchema,
    onSubmit: () => {
      handleNext();
    },
    enableReinitialize: true,
  });

  const handleClick = (value: string) => {
    const index = formik.values.coachType.findIndex((v) => v === value);
    let newValue;
    if (index >= 0) {
      const arr = [...formik.values.coachType];
      arr.splice(index, 1);
      newValue = arr;
    } else {
      newValue = [...formik.values.coachType, value];
    }
    dispatch(onboardingActions.setCoachTypes(newValue));
  };

  useEffect(() => {
    const initialTimer = setTimeout(() => {
      // Start the interval after 1600ms
      const interval = setInterval(() => {
        // Increment currentIndex every 800ms
        setCurrentIndex((prevIndex) => prevIndex + 1);
      }, 400);

      // Clear the interval when the component unmounts or when currentIndex reaches the end
      return () => clearInterval(interval);
    }, 1000);

    // Clear the initial timer when the component unmounts
    return () => clearTimeout(initialTimer);
  }, []);

  return (
    <Box minHeight={600} mb={4}>
      <form onSubmit={formik.handleSubmit} noValidate>
        <Grid container flexDirection="column" spacing={12}>
          <Grid item container gap="11px" flexDirection="column" textAlign="center">
            <Typography
              variant="title1Bold"
              color="primary.main"
              sx={{
                fontSize: {
                  xs: '20px',
                  md: '28px',
                  animation: `${opacityKeyframe} 0.5s ease forwards`,
                },
              }}
            >
              {`${t('therapistOnboarding.description.hello')} ${firstName}!`}
            </Typography>

            <Typography
              variant="title1Bold"
              color="primary.main"
              sx={{
                fontSize: { xs: '20px', md: '28px' },
                opacity: 0,
                animation: `${opacityKeyframe} 0.5s ease forwards`,
                animationDelay: '0.5s',
              }}
            >
              {t('therapistOnboarding.description.message')}
            </Typography>
          </Grid>
          <Grid item minHeight={320}>
            <Grid container spacing={1} justifyContent="center">
              {coachTypeOptions.slice(0, currentIndex + 1).map((coachType) => (
                <Grid item key={coachType.label}>
                  {formik.values.coachType.findIndex((v) => v === coachType.value) >= 0 ? (
                    <SelectedChip
                      label={t(coachType.label)}
                      size="medium"
                      variant="filled"
                      clickable
                      onClick={() => handleClick(coachType.value)}
                      sx={{ fontSize: { xs: '15px', md: '17px' }, paddingX: { xs: 2, md: 4 } }}
                    />
                  ) : (
                    <StyledChip
                      label={t(coachType.label)}
                      size="medium"
                      variant="outlined"
                      clickable
                      onClick={() => handleClick(coachType.value)}
                      sx={{ fontSize: { xs: '15px', md: '17px' }, paddingX: { xs: 2, md: 4 } }}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid
            item
            container
            justifyContent="center"
            sx={{
              opacity: 0,
              animation: `${opacityKeyframe} 0.8s ease forwards`,
              height: 48,
            }}
          >
            <StyledButton paddingX={32} disabled={formik.values.coachType.length === 0}>
              {t('therapistOnboarding.next')}
            </StyledButton>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}
